import { createSelector, createSlice } from "@reduxjs/toolkit";
import _ from "lodash";
import { getCoreplusPreload, getFirestorePreload, getSpecialists } from "../services/db4";
import { uploadAuPostcodeService } from "../services/upload";
import { getPractitionerPreferences } from "../services/practitioner";
import { getSpecialities } from "../services/specialists";
import { getAllClinics, getAllReferrers } from "../services/coreplusData";

const appSlice = createSlice({
  name: "app",
  initialState: {
    error: null,
    sites: [],
    clientGroups: [],
    practitioners: [],
    referrers: [],
    locations: [],
    clinics: [],
    admins: [],
    nurses: [],
    supervisors: [],
    specialtyTypes: [],
    practitionerPreferences: [],
    specialities: [],
    isLoggedIn: false,
    userId: null,
    specialistProfiles: [],
    specialistSchedulerDescriptions: {},
    specialists: [],
  },
  reducers: {
    setAppError(state, action) {
      if (!action.payload) {
        state.error = null;
        return;
      }
      state.error = action.payload.message || action.payload.debug || "Unknown Error";
    },
    setPreload(state, action) {
      const {
        sites,
        clientGroups,
        practitioners,
        referrers,
        locations,
        clinics,
        admins,
        nurses,
        supervisors,
        specialtyTypes,
        practitionerPreferences,
        specialities,
        specialists,
        specialistSchedulerDescriptions,
        specialistProfiles,
      } = action.payload;
      state.clientGroups = clientGroups;
      state.practitioners = _.sortBy(practitioners, [
        (p) => p.firstName?.trim().toLowerCase(),
        (p) => p.lastName?.trim().toLowerCase(),
      ]);
      state.referrers = _.sortBy(referrers, [(r) => r.firstName?.toLowerCase(), (r) => r.lastName?.toLowerCase()]);
      state.locations = _.sortBy(locations, [(l) => l.name]);
      state.clinics = _.sortBy(clinics, [(c) => c.name?.toLowerCase()]);
      state.admins = _.sortBy(admins, [(r) => r.firstName?.toLowerCase(), (r) => r.lastName?.toLowerCase()]);
      state.nurses = _.sortBy(nurses, [(r) => r.firstName?.toLowerCase(), (r) => r.lastName?.toLowerCase()]);
      state.supervisors = _.sortBy(supervisors, [(r) => r.firstName?.toLowerCase(), (r) => r.lastName?.toLowerCase()]);
      state.specialtyTypes = specialtyTypes;
      state.practitionerPreferences = practitionerPreferences;
      state.specialities = _.sortBy(specialities, [(r) => r.name]);
      state.specialistSchedulerDescriptions = specialistSchedulerDescriptions;
      state.sites = sites;
      state.specialists = _.sortBy(specialists, [(r) => r.firstName.toLowerCase(), (r) => r.lastName.toLowerCase()]);
      state.specialistProfiles = specialistProfiles;
    },
    setRefresh(state, action) {
      return { ...state, ...action.payload };
    },
    setLogin(state, action) {
      state.isLoggedIn = action.payload;
    },
    setUserId(state, action) {
      state.userId = action.payload;
    },
  },
});

export const { setAppError, setPreload, setLogin, setRefresh, setUserId } = appSlice.actions;

// Sync soft-deletes doctors that PMS/Coreplus no longer returns (isActive: false)
// instead of deleting them, so records that reference them still show the name.
// Pickers that choose or configure a person use the active selectors below;
// anything showing a person already stored on a record keeps state.app.* as is.
const isActivePerson = (person) => _.get(person, "isActive") !== false;

export const selectActivePractitioners = createSelector(
  (state) => state.app.practitioners,
  (practitioners) => _.filter(practitioners, isActivePerson)
);

// True when the id is in a list returned by selectActivePractitioners.
export const isActivePractitionerId = (activePractitioners, practitionerId) =>
  _.some(activePractitioners, { practitionerId });

// setPreload stores specialists as a sorted array and fetchSpecialists as an
// object keyed by id; normalise to the keyed shape the pickers look up by id.
export const selectSpecialistsById = createSelector(
  (state) => state.app.specialists,
  (specialists) => _.keyBy(Object.values(specialists || {}), "id")
);

export const selectActiveSpecialists = createSelector(selectSpecialistsById, (specialistsById) =>
  _.pickBy(specialistsById, isActivePerson)
);

export const fetchCoreplusPreload = () => async (dispatch) => {
  try {
    const result = await getCoreplusPreload();
    const preload = _.get(result, "data", {});
    dispatch(setPreload(preload));
  } catch (error) {
    dispatch(setAppError(error));
  }
};

export const fetchFirebasePreload = () => async (dispatch) => {
  try {
    const preload = await getFirestorePreload();
    dispatch(setPreload(preload));
  } catch (error) {
    dispatch(setAppError(error));
  }
};

export const fetchSpecialists = () => async (dispatch) => {
  try {
    const preload = await getSpecialists();
    dispatch(setRefresh(preload));
  } catch (error) {
    dispatch(setAppError(error));
  }
};

export const uploadAuPostcode = (data) => async (dispatch) => {
  try {
    await uploadAuPostcodeService(data);
  } catch (error) {
    dispatch(setAppError(error));
  }
};

export const fetchPractitionerPreferences = () => async (dispatch) => {
  try {
    const result = await getPractitionerPreferences();
    if (!_.isEmpty(result)) {
      dispatch(
        setRefresh({
          practitionerPreferences: result,
        })
      );
    } else {
      dispatch(setAppError());
    }
  } catch (error) {
    dispatch(setAppError(error));
  }
};

export const fetchReferrers = () => async (dispatch) => {
  try {
    const { data: referrers, error } = await getAllReferrers();
    if (!_.isEmpty(referrers)) {
      dispatch(setRefresh({ referrers }));
    } else {
      dispatch(setAppError(error));
    }
  } catch (error) {
    dispatch(setAppError(error));
  }
};

export const fetchClinics = () => async (dispatch) => {
  try {
    const { data: clinics, error } = await getAllClinics();
    if (!_.isEmpty(clinics)) {
      dispatch(setRefresh({ clinics }));
    } else {
      dispatch(setAppError(error));
    }
  } catch (error) {
    dispatch(setAppError(error));
  }
};

export const fetchSpecialities = () => async (dispatch) => {
  try {
    const result = await getSpecialities();
    if (!_.isEmpty(result)) {
      dispatch(
        setRefresh({
          specialities: result,
        })
      );
    } else {
      dispatch(setAppError());
    }
  } catch (error) {
    dispatch(setAppError(error));
  }
};

export default appSlice.reducer;
